import React, { useState } from 'react';
import { ALLOWED_FILES } from './FileExtensions';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, message, Upload } from 'antd';
import { API_ROOT as BASE_URL, authenticatedUploadProps } from '@partsbadger/utils';
const getStableUploadFileId = (file) => {
    const response = file.response;
    const responseId = typeof response === 'object' && response !== null && 'id' in response
        ? String(response.id ?? '')
        : '';
    const rawId = responseId.trim() !== '' ? responseId : String(file.uid);
    return rawId.replace(/[^a-zA-Z0-9_-]/g, '-');
};
const UploadFile = React.forwardRef((props, ref) => {
    const [countFiles, setCountFiles] = useState(0);
    const { multiple, onChange, onUpload, onRemove, value, fileType, renderAs, maxCount, maxSize, defaultFileList, customUrl, fileRowTestIdPrefix, fileRemoveTestIdPrefix, } = props;
    const runCustomBeforeUpload = (file, fileList) => {
        if (!props.beforeUpload) {
            return true;
        }
        const result = props.beforeUpload(file, fileList);
        if (result instanceof Promise) {
            return result.then(value => (value === false ? Upload.LIST_IGNORE : value));
        }
        return result === false ? Upload.LIST_IGNORE : result;
    };
    const handleChange = (info) => {
        const { file } = info;
        let fileList = info.fileList;
        if (maxSize !== undefined && file?.size) {
            const isLt2M = file?.size / 1024 / 1024 <= maxSize;
            if (!isLt2M) {
                fileList = [];
            }
        }
        if (!multiple) {
            fileList = info.fileList.slice(-1);
        }
        if (onChange) {
            onChange(fileList);
        }
        if (info.file.status === 'done' && !!onUpload) {
            onUpload(info.file);
        }
        if (info.file.status === 'removed' && !!onRemove) {
            onRemove(file);
        }
    };
    let url = `customer/files/`;
    let allowed_files = '';
    if (!props.accept) {
        switch (fileType) {
            case '3D':
                url = `customer/step-files/`;
                allowed_files = ALLOWED_FILES.allowed_3d.map(r => `.${r.toLowerCase()}`).toString();
                break;
            case '2D':
                url = `customer/drawing-files/`;
                allowed_files = ALLOWED_FILES.allowed_2d.map(r => `.${r.toLowerCase()}`).toString();
                break;
        }
    }
    else {
        switch (fileType) {
            case '3D':
                url = `customer/step-files/`;
                allowed_files = props.accept.map(r => `.${r.toLowerCase()}`).toString();
                break;
            case '2D':
                url = `customer/drawing-files/`;
                allowed_files = props.accept.map(r => `.${r.toLowerCase()}`).toString();
                break;
            default:
                allowed_files = props.accept.map(r => `.${r.toLowerCase()}`).toString();
                break;
        }
    }
    const allowed3dExtensions = ALLOWED_FILES.allowed_3d.map(r => r.toLowerCase());
    let otherProps = {};
    if (value && value.length > 0) {
        otherProps = {
            fileList: value,
        };
    }
    if (customUrl) {
        url = customUrl;
    }
    const uploadAuthProps = authenticatedUploadProps(BASE_URL + '/' + url);
    const showUploadList = fileRemoveTestIdPrefix && props.showUploadList === true
        ? {
            showRemoveIcon: true,
            removeIcon: (file) => (React.createElement("span", { "data-testid": `${fileRemoveTestIdPrefix}-${getStableUploadFileId(file)}` },
                React.createElement(DeleteOutlined, null))),
        }
        : props.showUploadList;
    const itemRender = fileRowTestIdPrefix
        ? (originNode, file) => (React.createElement("div", { "data-testid": `${fileRowTestIdPrefix}-${getStableUploadFileId(file)}`, "data-upload-file-id": getStableUploadFileId(file), "data-upload-file-name": file.name }, originNode))
        : undefined;
    if (renderAs == 'button') {
        return (React.createElement(Upload, { className: props.className, name: "file", ...uploadAuthProps, accept: allowed_files, ...otherProps, showUploadList: showUploadList, onChange: handleChange, itemRender: itemRender, style: props.styles, beforeUpload: (file, fileList) => {
                if (fileType === '3D') {
                    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
                    if (!allowed3dExtensions.includes(ext)) {
                        message.error({
                            content: `"${file.name}" is not a valid 3D file. Please upload a 3D file format.`,
                            duration: 8,
                            key: 'invalid-3d-file',
                        });
                        return Upload.LIST_IGNORE;
                    }
                }
                if (props.accept) {
                    const type = file.name.split('.').pop();
                    const file_type = props.accept.filter(f => f === type?.toLowerCase());
                    if (type?.toLowerCase() !== file_type[0]) {
                        message.error('The file type is not allowed, try again.', 3);
                        return Upload.LIST_IGNORE;
                    }
                }
                return runCustomBeforeUpload(file, fileList);
            } }, props.children ?? (React.createElement(Button, { size: 'small' },
            React.createElement(UploadOutlined, null),
            " Upload"))));
    }
    return (React.createElement(Upload.Dragger, { className: props.className, name: "file", ...uploadAuthProps, multiple: multiple === false ? false : true, accept: allowed_files, ...otherProps, showUploadList: showUploadList, onChange: handleChange, itemRender: itemRender, style: props.styles, maxCount: maxCount, defaultFileList: defaultFileList ?? [], onRemove: () => {
            if (countFiles >= 1) {
                setCountFiles(countFiles - 1);
            }
        }, beforeUpload: (file, fileList) => {
            if (fileType === '3D') {
                const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
                if (!allowed3dExtensions.includes(ext)) {
                    message.error({
                        content: `"${file.name}" is not a valid 3D file. Please upload a 3D file format.`,
                        duration: 8,
                        key: 'invalid-3d-file',
                    });
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSize !== undefined && file?.size) {
                const isLt2M = file?.size / 1024 / 1024 <= maxSize;
                if (!isLt2M) {
                    message.error(`File must be smaller than ${maxSize}MB!`, 10);
                    return false;
                }
            }
            if (maxCount !== undefined) {
                if (countFiles >= maxCount) {
                    message.error('Please be aware that you can only upload a maximum of 10 files.', 3);
                    return false;
                }
                setCountFiles(countFiles + fileList.length);
            }
            if (props.accept) {
                const type = file.name.split('.').pop();
                const file_type = props.accept.filter(file => file === type?.toLowerCase());
                if (type?.toLowerCase() !== file_type[0]) {
                    message.error('The file type is not allowed, try again.', 3);
                    return Upload.LIST_IGNORE;
                }
            }
            return runCustomBeforeUpload(file, fileList);
        } }, props.children ??
        (fileType === '3D' ? (React.createElement("div", { className: "flex flex-col justify-center items-center", style: { height: '309px' } },
            React.createElement(UploadOutlined, { style: { fontSize: '96px', color: '#405764' } }),
            React.createElement("p", { className: "font-bold text-2xl  text-center pt-6", style: { width: '227px' } }, "Select STEP, STP or IGES File"),
            React.createElement("p", { className: "text-base font-bold ml-1  pt-10", style: { color: '#595959' } },
                "Drop your files here or ",
                React.createElement("span", { className: "text-red-600 font-bold" }, "Browse")))) : (React.createElement("div", { className: "flex justify-center items-center align-middle", style: { height: '87px' } },
            React.createElement(UploadOutlined, { style: { fontSize: '34px', color: '#405764' } }),
            React.createElement("span", { className: "text-base ml-1" }, "Click or drag files"))))));
});
/**
 * Map the response of the api to a local format to be used in UploadFile component
 * @param file
 * @returns {{uid: *, response: {id: *, url: *}, name: *, status: string}}
 */
export const normalize_files = (file) => {
    if (!file) {
        return;
    }
    return {
        uid: file.id,
        name: file.filename,
        status: 'done',
        response: {
            id: file.id,
            url: file.file,
        },
    };
};
UploadFile.displayName = 'UploadFile';
export default UploadFile;
